// packages/console/src/components/panels/LiveProgramMonitor.tsx
//
// Program monitor for the LIVE console, ported from the program-monitor block
// of docs/design/ui_kits/aether-live/Transcoder.jsx. A 16:9 on-air preview with
// an on-air badge and a burnt-in timecode, driven by REAL stream state.
//
// The design shows "No program" whenever the session is not on air. That is the
// honest empty state (R10): no session, or a stopped/waiting/paused one, means
// no frames, so the preview veils and the timecode reads an em-dash rather than
// counting up from nothing.

import { Dot } from "../Parts";
import { EM_DASH, fmtDur } from "../../lib/format";
import type { LiveStream } from "../../api/live";

// timecode renders the session timecode (tcSeconds) as HH:MM:SS, or an em-dash
// when there is no session or the figure is absent.
function timecode(stream: LiveStream | null): string {
  if (!stream || stream.tcSeconds === null) return EM_DASH;
  return fmtDur(stream.tcSeconds);
}

export function LiveProgramMonitor({ stream }: { stream: LiveStream | null }) {
  const onAir = stream?.state === "on_air";
  const tc = timecode(stream);
  return (
    <div
      style={{
        width: 268,
        flex: "none",
        position: "relative",
        aspectRatio: "16 / 9",
        borderRadius: "var(--r-md)",
        overflow: "hidden",
        border: `1px solid ${onAir ? "rgba(229,51,78,.45)" : "var(--line)"}`,
        background: "var(--bg-void)",
      }}
    >
      {onAir ? (
        <>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(140deg,#1B2440,#080B10 70%)" }} />
          <div
            style={{
              position: "absolute",
              left: "12%",
              top: "18%",
              width: "46%",
              height: "54%",
              borderRadius: "50%",
              background: "radial-gradient(circle,rgba(47,107,246,.5),transparent 68%)",
            }}
          />
          <div
            style={{
              position: "absolute",
              right: "10%",
              bottom: "12%",
              width: "38%",
              height: "40%",
              background: "linear-gradient(150deg,#2C3A52,#141A24)",
            }}
          />
        </>
      ) : (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(3,5,6,.72)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            font: "500 11px var(--font-sans)",
            letterSpacing: ".14em",
            textTransform: "uppercase",
            color: "var(--fg3)",
          }}
        >
          No program
        </div>
      )}
      <div
        style={{
          position: "absolute",
          left: 9,
          top: 9,
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 8px",
          background: "rgba(3,5,6,.7)",
          borderRadius: "var(--r-xs)",
        }}
      >
        <Dot tone={onAir ? "onair" : "idle"} pulse={onAir} />
        <span
          style={{
            font: "600 9px var(--font-sans)",
            letterSpacing: ".14em",
            textTransform: "uppercase",
            color: "#fff",
          }}
        >
          Program
        </span>
      </div>
      <span
        style={{
          position: "absolute",
          right: 9,
          bottom: 8,
          font: "400 10px var(--font-mono)",
          color: "#fff",
          background: "rgba(3,5,6,.7)",
          padding: "3px 6px",
          borderRadius: "var(--r-xs)",
        }}
      >
        {tc}
      </span>
    </div>
  );
}
